import { Col, Flex, Form, Layout, message, Row, Typography, Image, Input, Button } from "antd";
import loginBg from "../assets/bg_loginbo_new.webp";
import logo from "../assets/logo_pam.webp";
import pallete from "../utils/pallete";
import { useAuth } from "../hooks/useAuth";
import { useEffect, useState } from "react";
import { authService } from "../services/auth.service";
import { useMutation } from "@tanstack/react-query";

export default function LoginPage() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { login: setLogin } = useAuth();

  // Determine if mobile based on window width
  const isMobile = windowWidth <= 768;

  // Detect window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loginMutation = useMutation({
    mutationFn: (data) => authService.login(data),
    onSuccess: ({ data }) => {
      setLogin(data);
      window.location.href = '/';
    },
    onError: (err) => {
      if (err.status == 401) {
        messageApi.error("Username atau Password salah");
        return;
      }
      messageApi.error("Terjadi Kesalahan");
    }
  });

  const handleSubmit = async (val) => loginMutation.mutate(val);

  // Render the welcome header for mobile devices (maintaining structure but hiding text)
  const renderWelcomeHeader = () => (
    <div
      style={{
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "bottom",
        backgroundOrigin: "padding-box",
        backgroundRepeat: "no-repeat",
        height: "150px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Flex
        vertical
        justify="center"
        align="center"
        style={{
          height: "100%",
          width: "100%",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Typography.Title
          level={3}
          style={{
            color: "transparent",
            marginBottom: 10,
            visibility: "hidden",
          }}
        >
          Selamat Datang,
        </Typography.Title>
        <Typography.Title
          level={3}
          style={{
            color: "transparent",
            marginTop: -15,
            visibility: "hidden",
          }}
        >
          Back Office PAM JAYA
        </Typography.Title>
      </Flex>
    </div>
  );

  return (
    <>
      {contextHolder}

      {/* Fixed copyright footer for mobile */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            textAlign: "center",
            padding: "10px 0",
            zIndex: 1000,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Typography.Text style={{ color: pallete.grey[600] }}>
            ©{new Date().getFullYear()}. PAMJAYA - All Rights Reserved.
          </Typography.Text>
        </div>
      )}

      <Layout
        style={{
          minHeight: "100vh",
          height: "100vh",
          position: "fixed",
          width: "100%",
          top: 0,
          left: 0,
          backgroundColor: "#fff",
          paddingBottom: isMobile ? "70px" : 0, // Add padding for fixed footer on mobile
        }}
      >
        <Row
          style={{
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Left Column - Background Image (only on desktop) */}
          {!isMobile ? (
            <Col
              span={12}
              style={{
                backgroundImage: `url(${loginBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                height: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Typography.Title level={2} style={{ margin: 'auto', fontWeight: 700, color: 'transparent', textAlign: 'center' }}>
                Selamat Datang, <br /> Back Office PAM JAYA
              </Typography.Title>
            </Col>
          ) : (
            // Mobile header with background
            renderWelcomeHeader()
          )}

          {/* Right Column - Login Form */}
          <Col
            span={isMobile ? 24 : 12}
            style={{
              height: isMobile ? 'calc(100% - 150px)' : '100%',
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: "30px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 450,
                  margin: "0 auto",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Flex justify="center" style={{ marginBottom: 18 }}>
                  <Image
                    src={logo}
                    alt="PAM JAYA"
                    style={{ width: 90 }}
                    preview={false}
                  />
                </Flex>

                <Typography.Title
                  level={3}
                  style={{
                    fontWeight: 700,
                    margin: 0,
                    textAlign: 'center',
                    marginBottom: 12
                  }}
                >
                  Back Office: Website Kepelangganan PAM JAYA
                </Typography.Title>

                <Typography.Text
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: pallete.grey[600],
                    display: 'inline-block',
                    marginBottom: 22,
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  Silahkan masukkan Username dan Password anda
                </Typography.Text>

                <Form
                  layout="vertical"
                  requiredMark={false}
                  form={form}
                  onFinish={handleSubmit}
                  style={{ width: "100%" }}
                >
                  <Form.Item
                    label="Username"
                    name="username"
                    rules={[{ required: true }]}
                    style={{ fontWeight: 600, marginBottom: 20 }}
                  >
                    <Input size="large" />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true }]}
                    style={{ fontWeight: 600, marginBottom: 25 }}
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Button
                    loading={loginMutation.isPending}
                    disabled={loginMutation.isPending}
                    htmlType="submit"
                    type="primary"
                    size="large"
                    block
                  >
                    Masuk
                  </Button>
                </Form>
              </div>
            </div>

            {/* Desktop Only Copyright */}
            {!isMobile && (
              <Typography.Text
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "20px 0",
                  color: pallete.grey[600],
                }}
              >
                ©{new Date().getFullYear()}. PAMJAYA - All Rights Reserved.
              </Typography.Text>
            )}
          </Col>
        </Row>
      </Layout>
    </>
  );
}